import { get, noop } from 'lodash';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { SEARCH_DEBOUNCE_MS } from '~/common/constants/debounces';
import EcBooleanTextSearchPopup from '~/components/Shared/EcBooleanTextSearchPopup/EcBooleanTextSearchPopup';
import { Box } from '~/eds/src/components/Box/Box';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { SearchInput } from '~/eds/src/components/SearchInput/SearchInput';
import { Text } from '~/eds/src/components/Text/Text';
import { useDebounce } from '~/eds/src/hooks/useDebounce';
import { useToggle } from '~/eds/src/hooks/useToggle';
import { Nullable } from '~/types/Nullable';

type Props = {
  disabled?: boolean;
  isSubmitting?: boolean;
  includeOperatorButton?: boolean;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  validateInput: (value: string) => Promise<string>;
  onValidationError?: (message: string) => void;
};

const BaseSearchBar = ({
  disabled,
  isSubmitting,
  includeOperatorButton = true,
  placeholder,
  value,
  onChange,
  onSubmit,
  validateInput,
  onValidationError = noop,
}: Props) => {
  const [showSyntaxPopup, _toggle, showOperatorsInfo, hideOperatorsInfo] =
    useToggle(false);
  const [error, setError] = useState('');
  const intl = useIntl();

  const defaultPlaceholder =
    placeholder ??
    intl.formatMessage({
      id: 'BaseSearchBar.defaultPlaceholder',
      defaultMessage: 'Search filenames and content',
      description: 'Default placeholder text for search input',
    });

  const isInputValueInvalid = value && error;

  useEffect(() => {
    onValidationError(error);
  }, [error, onValidationError]);

  const validateQuery = useDebounce(
    async (query: string) => {
      try {
        const error = await validateInput(query);
        setError(error);
      } catch (e) {
        const errorMessage = get(
          e,
          'response.data.detail',
          intl.formatMessage({
            id: 'BaseSearchBar.validationError',
            defaultMessage: 'Something went wrong, please try again later.',
            description: 'Error message when search query validation fails',
          }),
        );
        setError(errorMessage);
      }
    },
    SEARCH_DEBOUNCE_MS,
    [intl, validateInput],
  );

  useEffect(() => {
    if (value) {
      validateQuery(value);
    }
  }, [value, validateQuery]);

  const handleOnKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !isInputValueInvalid) {
      onSubmit();
    }
  };

  const renderSearchInputAppendix = () => {
    if (!isInputValueInvalid && includeOperatorButton && value.length) {
      const keyToPressName =
        navigator.platform.toUpperCase().indexOf('MAC') >= 0
          ? intl.formatMessage({
              id: 'BaseSearchBar.returnKey',
              defaultMessage: 'Return',
              description: 'Mac keyboard return key name',
            })
          : intl.formatMessage({
              id: 'BaseSearchBar.enterKey',
              defaultMessage: 'Enter',
              description: 'PC keyboard enter key name',
            });
      return (
        <Box mt={1}>
          <Text color="text.secondary" variant="tiny">
            <FormattedMessage
              id="BaseSearchBar.pressKeyToSearch"
              defaultMessage="Press <strong>{keyName}</strong> to run search."
              description="Instruction to press a key to run search"
              values={{
                strong: (chunks) => <strong>{chunks}</strong>,
                keyName: keyToPressName,
              }}
            />
          </Text>
        </Box>
      );
    } else {
      return null;
    }
  };

  const handleChange = (value: Nullable<string>) => {
    onChange(value ?? '');
  };

  return (
    <Box w="100%">
      <Layout direction="column">
        <Layout>
          <Box w="100%">
            <SearchInput
              info={
                includeOperatorButton
                  ? {
                      onClick: showOperatorsInfo,
                      tooltip: intl.formatMessage({
                        id: 'BaseSearchBar.operatorsTooltip',
                        defaultMessage: 'Operators',
                        description: 'Tooltip for operators button',
                      }),
                    }
                  : undefined
              }
              name="boolean-search"
              error={isInputValueInvalid}
              placeholder={defaultPlaceholder}
              value={value}
              onChange={handleChange}
              onKeyDown={handleOnKeyUp}
              onBlur={() => {
                if (value === '') {
                  onSubmit();
                }
              }}
              disabled={disabled || isSubmitting}
            />
          </Box>
        </Layout>
        {renderSearchInputAppendix()}
      </Layout>
      {showSyntaxPopup && (
        <EcBooleanTextSearchPopup handleOnCloseClick={hideOperatorsInfo} />
      )}
    </Box>
  );
};

export default BaseSearchBar;
